import { z } from "zod";
import { FEATURE_CODES } from "../catalog/features.ts";
import { QUOTA_RESOURCES } from "../catalog/quotas.ts";

/**
 * A merchant looking at their own subscription (`billing:read`,
 * `billing:manage`).
 *
 * The operator console has the full billing surface — contracts, settlements,
 * reconciliation. This is the merchant's half of it: what they are on, what it
 * includes, what they have used against it, when it ends, and what they have
 * paid. `06-saas-admin.md` keeps the two apart deliberately, and the shapes
 * here are narrower than the operator's on purpose: a merchant has no business
 * seeing a partner's commission on their own subscription.
 */

const Money = z.string().regex(/^-?\d+$/, "minor units, as digits");

export const BillingQuotaSchema = z.object({
  resource: z.enum(QUOTA_RESOURCES),
  /** Null is unmetered — never a sentinel a merchant could one day exceed. */
  limit: z.number().int().nullable(),
  used: z.number().int(),
});

export const BillingSummarySchema = z.object({
  planCode: z.string(),
  planName: z.string(),
  tier: z.number().int(),
  status: z.string(),
  /** What it renews at, per month. */
  monthlyPrice: Money,
  currency: z.string(),
  startsAt: z.string(),
  /** Null when the plan is permanent — there is no expiry to renew against. */
  expiresAt: z.string().nullable(),
  isPermanent: z.boolean(),
  /** Negative once it has lapsed, which is the number worth showing. */
  daysRemaining: z.number().int().nullable(),
  features: z.array(z.enum(FEATURE_CODES)),
  quotas: z.array(BillingQuotaSchema),
});
export type BillingSummary = z.infer<typeof BillingSummarySchema>;

export const BillingTransactionSchema = z.object({
  id: z.uuid(),
  serialNo: z.string(),
  description: z.string().nullable(),
  amount: Money,
  currency: z.string(),
  paidAt: z.string(),
  method: z.string().nullable(),
});

/**
 * A merchant renewing their own subscription.
 *
 * Named apart from the operator's `RenewSubscriptionSchema`, which is a
 * different body for a different actor — that one names a plan version and a
 * discount, and neither is a merchant's to choose.
 *
 * The plan cannot be chosen here: moving between tiers is a contract change and
 * `06-saas-admin.md` puts that in the operator console with a signature behind
 * it. This extends the term at what the merchant is already on, which is the
 * thing they can honestly do without one.
 */
export const RenewOwnSubscriptionSchema = z.object({
  months: z.number().int().min(1).max(60),
  cycle: z.enum(["monthly", "yearly"]).default("monthly"),
});
